// routes.tsx
import { Navigate, NonIndexRouteObject } from 'react-router-dom';
import Dashboard from './components/pages/dashboard/dashboard';
import Readers from './components/pages/reader/readerList';
import ReaderDetail from './components/pages/reader/readerDetails';
import ReaderReportDetail from './components/pages/readerReport/readerReportDetail';
import Devices from './components/pages/device/deviceList';
import Sightings from './components/pages/sightings/sightings';
import Authenticate from './components/pages/authenticate/login';
import Facilities from './components/pages/facilities/facilityList';
import ScanSessions from './components/pages/scanSessions/scanSessions';
import ScanDetail from './components/pages/scanSessions/scanDetails';
import Geofences from './components/pages/geofence/geofences';
import Equipment from './components/pages/equipment/equipmentList';
import Settings from './components/pages/settings/settingList';
import Labels from './components/pages/labels/labelList';
import MobileReaderReportDetail from './components/pages/readerReport/mobile/readerReportDetail';
import DeviceDetail from './components/pages/device/deviceDetails';
import Support from './components/pages/support/support';
import Account from './components/pages/account/account';
import DeleteAccount from './components/pages/account/deleteaccount';
import EquipmentDetails from './components/pages/equipment/equipmentDetails';
import Admission from './components/pages/account/admission';
import AppUsers from './components/pages/account/appUsers';
import Policies from './components/pages/authorization/policy/policies';
import AppUserOverview from './components/pages/authorization/appuser/appUserOverview';
import RoleOverview from './components/pages/authorization/role/roleOverview';
import PolicyOverview from './components/pages/authorization/policy/policyOverview';
import { AppPermissions } from './constants/Permissions';
import PairingHistoryList from './components/pages/pairinghistory/pairingHistory';
import Search from './components/pages/search/search';
import ScopeTemplates from './components/pages/scope/scopetemplates';
import EditScopeTemplate from './components/pages/scope/editscopetemplate';
import TasksQueue from './components/pages/tasksQueue/tasksQueue';
import Languages from './components/pages/languages/Languages';
import TaskDetail from './components/pages/tasksQueue/taskDetail';
import ManageInventory from './components/pages/manageinventory/ManageInventory';
import Collections from './components/pages/collections/Collections';
import CollectionDetails from './components/pages/collections/CollectionDetails';
import ContractOverview from './components/pages/contracts/ContractOverview';
import { AppReleases } from './components/pages/appReleases/AppReleases';
import Roles from './components/pages/authorization/role/roles';
import { Transfers } from './components/pages/transfers/Transfers';
import { TransferDetails } from './components/pages/transfers/TransferDetails';
import VisitHistoryDetail from './components/pages/device/VisitHistoryDetail';
import Companies from './components/pages/companies/Companies';
import UserGuide from './components/pages/userGuide/UserGuide';
import Sandbox from './components/pages/sandbox/Sandbox';
import Templates from './components/pages/templates/Templates';
import SuperSentinelActivation from './components/pages/SuperSentinelActivation/SuperSentinelActivation';
import Downloads from './components/pages/Downloads/Downloads';
import StockDetails from './components/pages/StockDetails/StockDetails';
import Visits from './components/pages/visits/Visits';
import AuthService from './services/auth.service';

// Import other components for your routes

export interface CustomRouteObject extends NonIndexRouteObject {
  label?: string;
  isDefault?: true;
  showInToolbar: boolean;
  isAuthGuarded: boolean;
  guardedPermission?: string;
}

const externalOnly = (element: JSX.Element) => {
  return AuthService.getStoredUserProfile()?.isInternal === false ? element : <Navigate to='/dashboard' replace />;
};

const routes: CustomRouteObject[] = [
  {
    path: 'account',
    element: <Account />,
    label: 'My Account',
    showInToolbar: false,
    isAuthGuarded: true,
    children: [{ path: 'delete', element: <DeleteAccount /> }],
  },
  {
    path: 'admission',
    element: <Admission />,
    label: 'Admission',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.User.Manage,
  },
  { path: 'login', element: <Authenticate />, showInToolbar: false, isAuthGuarded: false },
  { path: 'dashboard', isDefault: true, element: <Dashboard />, label: 'Dashboard', showInToolbar: true, isAuthGuarded: true },
  {
    path: 'stockDetails',
    isDefault: true,
    element: <StockDetails />,
    label: 'Stock Details',
    showInToolbar: true,
    isAuthGuarded: true,
    // guardedPermission: AppPermissions.StockDetails.Read,
  },
  {
    path: 'visits',
    element: externalOnly(<Visits />),
    label: 'Visits',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Visit.Read,
  },
  {
    path: 'visits/:id',
    element: externalOnly(<DeviceDetail />),
    label: 'Visits',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Visit.Read,
  },
  {
    path: 'tasksqueue',
    isDefault: true,
    element: <TasksQueue />,
    label: 'TasksQueue',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.TasksQueue.Read,
  },
  {
    path: 'tasksqueue/:id',
    element: <TaskDetail />,
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.TasksQueue.Read,
  },
  {
    path: 'devices',
    element: <Devices />,
    label: 'Devices',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Device.Read,
  },
  { path: 'devices/:id', element: <DeviceDetail />, showInToolbar: false, isAuthGuarded: true, guardedPermission: AppPermissions.Device.Read },
  {
    path: 'devices/visit-log/:id',
    element: <VisitHistoryDetail />,
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Device.Read,
  },
  {
    path: 'equipment',
    element: <Equipment />,
    label: 'Equipment',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Equipment.Read,
  },
  {
    path: 'equipment/:id',
    element: <EquipmentDetails />,
    label: '',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Equipment.Read,
  },
  {
    path: 'facilities',
    element: <Facilities />,
    label: 'Facilities',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Facility.Read,
  },
  {
    path: 'geofences',
    element: <Geofences />,
    label: 'Geofences',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Geofence.Read,
  },
  { path: 'monitoring', element: <Dashboard />, label: 'monitoring', showInToolbar: true, isAuthGuarded: true },
  {
    path: 'scansessions',
    element: <ScanSessions />,
    label: 'ScanSessions',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.ScanSession.Read,
  },
  {
    path: 'scan/:scanSessionId',
    element: <ScanDetail />,
    label: 'Scan Detail',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.ScanSession.Read,
  },
  {
    path: 'collections',
    element: <Collections />,
    label: 'Collections',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Collections.Read,
  },
  {
    path: 'collections/:id',
    element: <CollectionDetails />,
    label: 'Collection Details',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Collections.Read,
  },
  {
    path: 'transfers',
    element: <Transfers />,
    label: 'Transfers',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.LucTransfer.Read,
  },
  {
    path: 'transfers/:transferId',
    element: <TransferDetails />,
    label: 'Transfer Details',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.LucTransfer.Read,
  },
  {
    path: 'app-releases',
    element: <AppReleases />,
    label: 'App Releases',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Apk.Read,
  },
  {
    path: 'downloads',
    element: <Downloads />,
    label: 'Downloads',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Export.Read,
  },
  {
    path: 'scopetemplates',
    element: <ScopeTemplates />,
    label: 'Scope Templates',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.System.Admin,
  },
  {
    path: 'scopetemplates/:id',
    element: <EditScopeTemplate />,
    label: 'Scope Templates',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.System.Admin,
  },
  { path: 'search/:wildcard', element: <Search />, showInToolbar: false, isAuthGuarded: true },
  {
    path: 'settings',
    element: <Settings />,
    label: 'Settings',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Setting.Read,
  },
  {
    path: 'languages',
    element: <Languages />,
    label: 'Languages',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Language.Manage,
  },
  {
    path: 'templates',
    element: <Templates />,
    label: 'Templates',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Language.Manage,
  },
  {
    path: 'super-sentinel-activation',
    element: <SuperSentinelActivation />,
    label: 'SuperSentinelActivation',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.SuperSentinel.Manage,
  },
  {
    path: 'manageinventory',
    element: <ManageInventory />,
    label: 'ManageInventory',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Device.Import,
  },
  { path: 'labels', element: <Labels />, label: 'Labels', showInToolbar: true, isAuthGuarded: true, guardedPermission: AppPermissions.Label.Edit },
  {
    path: 'sightings',
    element: <Sightings />,
    label: 'Sightings',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.SightingLatest.Read,
  },
  { path: 'support', element: <Support />, label: 'Support', showInToolbar: true, isAuthGuarded: false },
  {
    path: 'pairinghistory',
    element: <PairingHistoryList />,
    label: 'Pairing History',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Pairing.Read,
  },
  {
    path: 'policies',
    element: <Policies />,
    label: 'Policies',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.System.Admin,
  },
  {
    path: 'policies/:id',
    element: <PolicyOverview />,
    label: 'Policy',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.System.Admin,
  },
  {
    path: 'readers',
    element: <Readers />,
    label: 'Readers',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Reader.Read,
  },
  {
    path: 'readers/:identifier',
    element: <ReaderDetail />,
    label: 'Reader Detail',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Reader.Read,
  },
  {
    path: 'report/:reportId',
    element: <ReaderReportDetail />,
    label: 'Reader Report Detail',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.ReaderReport.Read,
  },
  { path: 'roles', element: <Roles />, label: 'Roles', showInToolbar: true, isAuthGuarded: true, guardedPermission: AppPermissions.System.Admin },
  {
    path: 'roles/:id',
    element: <RoleOverview />,
    label: 'Role',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.System.Admin,
  },
  {
    path: 'user-guide',
    element: <UserGuide />,
    label: 'User Guide',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.UserGuide.Manage,
  },
  { path: 'users', element: <AppUsers />, label: 'Users', showInToolbar: true, isAuthGuarded: true, guardedPermission: AppPermissions.User.Manage },
  {
    path: 'users/:id',
    element: <AppUserOverview />,
    label: 'User',
    showInToolbar: false,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.User.Manage,
  },
  {
    path: 'contracts',
    element: <ContractOverview />,
    label: 'Contracts',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Contract.Read,
  },
  {
    path: 'companies',
    element: <Companies />,
    label: 'Companies',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.Company.Read,
  },
  {
    path: 'sandbox',
    element: <Sandbox />,
    label: 'Sandbox',
    showInToolbar: true,
    isAuthGuarded: true,
    guardedPermission: AppPermissions.System.Admin,
  },
];

const mobileRoutes: CustomRouteObject[] = [
  { path: 'report/:reportId', element: <MobileReaderReportDetail />, label: 'Reader Report Detail', showInToolbar: false, isAuthGuarded: true },
  {
    path: 'account',
    element: <Account />,
    label: 'My Account',
    showInToolbar: false,
    isAuthGuarded: true,
    children: [
      {
        path: 'delete',
        element: <DeleteAccount />,
      },
    ],
  },
];

export { routes, mobileRoutes };
